// Enhanced ContactForm.jsx with detailed error logging and debugging

import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import { Send, CheckCircle, AlertCircle, Loader, Info } from 'lucide-react';
import { Button } from '../../common/UI';

const ContactForm = () => {
  const form = useRef();
  const [formData, setFormData] = useState({
    user_name: '',
    user_email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState('idle');
  const [errors, setErrors] = useState({});
  const [debugInfo, setDebugInfo] = useState(null);

  // Enhanced debugging - check environment variables
  React.useEffect(() => {
    console.log('🔍 Environment Variables Check:');
    console.log('Service ID:', process.env.REACT_APP_EMAILJS_SERVICE_ID ? '✅ Set' : '❌ Missing');
    console.log('Template ID:', process.env.REACT_APP_EMAILJS_TEMPLATE_ID ? '✅ Set' : '❌ Missing');
    console.log('Auto-reply Template ID:', process.env.REACT_APP_EMAILJS_AUTOREPLY_TEMPLATE_ID ? '✅ Set' : '❌ Missing');
    console.log('Public Key:', process.env.REACT_APP_EMAILJS_PUBLIC_KEY ? '✅ Set' : '❌ Missing');
    console.log('Creator Email:', process.env.REACT_APP_CREATOR_EMAIL ? '✅ Set' : '❌ Missing');

    // Check if all required variables are present
    const requiredVars = [
      'REACT_APP_EMAILJS_SERVICE_ID',
      'REACT_APP_EMAILJS_TEMPLATE_ID', 
      'REACT_APP_EMAILJS_PUBLIC_KEY',
      'REACT_APP_CREATOR_EMAIL'
    ];

    const missingVars = requiredVars.filter(varName => !process.env[varName]);
    
    if (missingVars.length > 0) {
      setDebugInfo({
        type: 'config_error',
        message: `Missing environment variables: ${missingVars.join(', ')}`,
        solution: 'Check your .env.local file and restart your dev server'
      });
    }

    // Initialize EmailJS only if we have the public key
    if (process.env.REACT_APP_EMAILJS_PUBLIC_KEY) {
      emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
      console.log('✅ EmailJS initialized successfully');
    } else {
      console.error('❌ Cannot initialize EmailJS - missing public key');
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.user_name.trim()) {
      newErrors.user_name = 'Name is required';
    }

    if (!formData.user_email.trim()) {
      newErrors.user_email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.user_email)) {
      newErrors.user_email = 'Please enter a valid email address';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    
    console.log('🚀 Form submission started');
    
    if (!validateForm()) {
      console.log('❌ Form validation failed');
      return;
    }

    // Clear any previous debug info
    setDebugInfo(null);
    setStatus('loading');

    // Check configuration before sending
    const requiredEnvVars = {
      serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID,
      templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
      creatorEmail: process.env.REACT_APP_CREATOR_EMAIL
    };

    console.log('📧 EmailJS Configuration:');
    console.log('Service ID:', requiredEnvVars.serviceId);
    console.log('Template ID:', requiredEnvVars.templateId);
    console.log('Public Key:', requiredEnvVars.publicKey ? `${requiredEnvVars.publicKey.substring(0, 8)}...` : 'Missing');
    console.log('Creator Email:', requiredEnvVars.creatorEmail);

    // Check for missing configuration
    const missingConfig = Object.entries(requiredEnvVars)
      .filter(([key, value]) => !value)
      .map(([key]) => key);

    if (missingConfig.length > 0) {
      console.error('❌ Missing configuration:', missingConfig);
      setStatus('error');
      setDebugInfo({
        type: 'config_error',
        message: `Missing configuration: ${missingConfig.join(', ')}`,
        solution: 'Check your .env.local file and restart your development server'
      });
      return;
    }

    try {
      console.log('📨 Sending notification email...');
      
      // Send notification email to you
      const notificationResult = await emailjs.sendForm(
        requiredEnvVars.serviceId,
        requiredEnvVars.templateId,
        form.current,
        requiredEnvVars.publicKey
      );

      console.log('✅ Notification email sent:', notificationResult);

      // Send auto-reply if template ID is provided
      if (process.env.REACT_APP_EMAILJS_AUTOREPLY_TEMPLATE_ID) {
        console.log('📨 Sending auto-reply email...');
        
        const autoReplyResult = await emailjs.send(
          requiredEnvVars.serviceId,
          process.env.REACT_APP_EMAILJS_AUTOREPLY_TEMPLATE_ID,
          {
            to_name: formData.user_name,
            to_email: formData.user_email,
            subject: formData.subject,
            reply_to: formData.user_email
          },
          requiredEnvVars.publicKey
        );

        console.log('✅ Auto-reply email sent:', autoReplyResult);
      } else {
        console.log('⚠️ Auto-reply template ID not configured, skipping auto-reply');
      }

      setStatus('success');
      setFormData({
        user_name: '',
        user_email: '',
        subject: '',
        message: ''
      });
      
      console.log('🎉 Email sending completed successfully');
      
      // Reset success status after 5 seconds
      setTimeout(() => {
        setStatus('idle');
      }, 5000);

    } catch (error) {
      console.error('❌ EmailJS Error Details:', error);
      
      // Enhanced error handling
      let errorMessage = 'Failed to send message. ';
      let solution = '';

      if (error.status === 422) {
        errorMessage += 'Invalid template or service configuration.';
        solution = 'Check your EmailJS template IDs and service ID';
      } else if (error.status === 400) {
        errorMessage += 'Bad request - check your form data.';
        solution = 'Verify all form fields are properly filled';
      } else if (error.status === 401) {
        errorMessage += 'Unauthorized - check your EmailJS public key.';
        solution = 'Verify your EmailJS public key is correct';
      } else if (error.text) {
        errorMessage += error.text;
      } else {
        errorMessage += 'Please try again or contact me directly.';
      }

      setStatus('error');
      setDebugInfo({
        type: 'send_error',
        message: errorMessage,
        solution: solution,
        details: {
          status: error.status,
          text: error.text,
          name: error.name
        }
      });
      
      // Reset error status after 10 seconds
      setTimeout(() => {
        setStatus('idle');
        setDebugInfo(null);
      }, 10000);
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'success':
        return {
          icon: <CheckCircle className="w-5 h-5 text-green-500" />,
          message: 'Message sent successfully! I\'ll get back to you within 24 hours.',
          bgColor: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
          textColor: 'text-green-800 dark:text-green-300'
        };
      case 'error':
        return {
          icon: <AlertCircle className="w-5 h-5 text-red-500" />,
          message: debugInfo?.message || 'Failed to send message. Please try again or contact me directly.',
          bgColor: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
          textColor: 'text-red-800 dark:text-red-300'
        };
      default:
        return null;
    }
  };

  const statusMessage = getStatusMessage();

  return (
    <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        Send me a message
      </h3>

      {/* Debug Information */}
      {debugInfo && (
        <div className="mb-6 p-4 rounded-lg border border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800">
          <div className="flex items-start space-x-2">
            <Info className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div className="text-yellow-800 dark:text-yellow-300">
              <p className="font-medium mb-1">Debug Information:</p>
              <p className="text-sm mb-2">{debugInfo.message}</p>
              {debugInfo.solution && (
                <p className="text-sm font-medium">Solution: {debugInfo.solution}</p>
              )}
              {debugInfo.details && (
                <details className="mt-2">
                  <summary className="text-sm cursor-pointer">Technical Details</summary>
                  <pre className="text-xs mt-1 p-2 bg-yellow-100 dark:bg-yellow-900/40 rounded">
                    {JSON.stringify(debugInfo.details, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status Message */}
      {statusMessage && (
        <div className={`mb-6 p-4 rounded-lg border ${statusMessage.bgColor}`}>
          <div className={`flex items-center space-x-2 ${statusMessage.textColor}`}>
            {statusMessage.icon}
            <span className="font-medium">{statusMessage.message}</span>
          </div>
        </div>
      )}

      <form ref={form} onSubmit={sendEmail} className="space-y-6">
        {/* Hidden field for your email */}
        <input 
          type="hidden" 
          name="to_email" 
          value={process.env.REACT_APP_CREATOR_EMAIL || ''} 
        />

        {/* Name Field */}
        <div>
          <label htmlFor="user_name" className="block text-sm font-medium text-left text-gray-700 dark:text-gray-300 mb-2">
            Your Name *
          </label>
          <input
            type="text"
            id="user_name"
            name="user_name"
            value={formData.user_name}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
              errors.user_name 
                ? 'border-red-500 bg-red-50 dark:bg-red-900/20' 
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
            } text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400`}
            placeholder="Enter your full name"
            disabled={status === 'loading'}
          />
          {errors.user_name && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.user_name}</p>
          )}
        </div>

        {/* Email Field */}
        <div>
          <label htmlFor="user_email" className="block text-left text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Your Email *
          </label>
          <input
            type="email"
            id="user_email"
            name="user_email"
            value={formData.user_email}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
              errors.user_email 
                ? 'border-red-500 bg-red-50 dark:bg-red-900/20' 
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
            } text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400`}
            placeholder="your.email@example.com"
            disabled={status === 'loading'}
          />
          {errors.user_email && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.user_email}</p>
          )}
        </div>

        {/* Subject Field */}
        <div>
          <label htmlFor="subject" className="block text-left text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Subject *
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
              errors.subject 
                ? 'border-red-500 bg-red-50 dark:bg-red-900/20' 
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
            } text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400`}
            placeholder="What's this about?"
            disabled={status === 'loading'}
          />
          {errors.subject && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.subject}</p>
          )}
        </div>

        {/* Message Field */}
        <div>
          <label htmlFor="message" className="block text-left text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            rows={6}
            value={formData.message}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none ${
              errors.message 
                ? 'border-red-500 bg-red-50 dark:bg-red-900/20' 
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
            } text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400`}
            placeholder="Tell me about your project, idea, or just say hello!"
            disabled={status === 'loading'}
          />
          {errors.message && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.message}</p>
          )}
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {formData.message.length}/500 characters
          </p>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={status === 'loading'}
          className="w-full flex items-center justify-center space-x-2 py-4"
          size="lg"
        >
          {status === 'loading' ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              <span>Sending Message...</span>
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              <span>Send Message</span>
            </>
          )}
        </Button>
      </form>

      {/* Form Footer */}
      <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
          Your information is secure and will only be used to respond to your inquiry.
        </p>
        
        {/* Debug Mode Toggle (only in development) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => {
                console.log('🔍 Current Environment Variables:');
                console.log('NODE_ENV:', process.env.NODE_ENV);
                Object.keys(process.env)
                  .filter(key => key.startsWith('REACT_APP_EMAILJS'))
                  .forEach(key => {
                    console.log(`${key}:`, process.env[key] ? '✅ Set' : '❌ Missing');
                  });
              }}
              className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Debug: Check Environment Variables (Dev Only)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactForm;